import { ChevronLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useInDesktopShell } from '@/shared/lib/desktopLayout';
import { isTelegram } from '@/shared/lib/platform';
import { useTelegramBackButton } from '@/shared/lib/telegram';
import { SwipeBackZone } from './SwipeBackZone';

interface BackGestureProps {
  enabled: boolean;
  onBack: () => void;
  /**
   * false — в браузере своя стрелка не нужна: на экране уже есть видимая
   * кнопка «назад» (футер онбординга, `FlowShell`).
   */
  webButton?: boolean;
}

/**
 * Нативная кнопка «назад» Telegram и свайп от левого края — всегда парой,
 * одним вызовом. Раньше их подключали по отдельности (`useTelegramBackButton`
 * в одном месте, `SwipeBackZone` — в другом), и на страницах со схемой
 * список/деталь (`MistakesPage`, `MyListsManagePage`), где кнопку держит сам
 * родитель через `useTelegramBackButton` напрямую, а вложенные `PageLayout`
 * идут с `nativeBackButton={false}`, свайп внутри `PageHeader` было
 * подключать некому — жест работал только там, где `PageHeader` сам владел
 * кнопкой (например, в `SessionShell`).
 *
 * В браузере (веб-версия) нативной кнопки нет — вместо неё своя стрелка в
 * левой колонке шапки, там, где в Telegram стоит системная. Здесь, а не в
 * `PageHeader`: через `BackGesture` проходят все экраны с «назад», включая
 * те, где шапка не владеет кнопкой (чат, проверка уровня, список/деталь).
 */
export function BackGesture({ enabled, onBack, webButton = true }: BackGestureProps) {
  useTelegramBackButton(enabled, onBack);
  // В десктопном каркасе стрелка — в строке заголовка (`PageHeader`).
  const inDesktopShell = useInDesktopShell();
  if (!enabled) return null;
  return (
    <>
      {/* Свой свайп — только в Telegram. В браузере у Safari и Chrome свой
          жест «назад» от края, а наша полоса забирает касание себе
          (`preventDefault`) и дралась с ним — всё дёргалось (2026-10-28). */}
      {isTelegram() && <SwipeBackZone onBack={onBack} />}
      {webButton && !isTelegram() && !inDesktopShell && <WebBackButton onBack={onBack} />}
    </>
  );
}

function WebBackButton({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation('BackGesture');
  return (
    <button
      type="button"
      onClick={onBack}
      aria-label={t('back')}
      // z-40 — над шапкой (`PageHeader`, z-30), в её левой колонке.
      className="fixed top-[calc(var(--status-bar-top)+(var(--header-band-height)-2.5rem)/2)] left-2 z-40 flex size-10 items-center justify-center rounded-full text-ink outline-none hover:bg-ink/5 focus-visible:ring-2 focus-visible:ring-signal-blue/40 active:bg-ink/10"
    >
      <ChevronLeft className="size-6" aria-hidden="true" />
    </button>
  );
}
